import {
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { keyBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FieldSection, FieldSectionType } from 'api/field-sections/types';
import { Field } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateFieldSectionOrderMutationV2 as useUpdateFieldSectionOrderMutation } from 'queries/field-sections';
import { FieldSectionMapping } from './section-list';
import {
  FieldMappingContainer,
  FieldRow,
  SectionTableEmptyState,
} from './sortable-section-list-components';

interface Props {
  sectionList: FieldSectionMapping[];
  sectionType: FieldSectionType;
}

const dropAnimation: DropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

interface SortableItemProps {
  disabled?: boolean;
  id: number;
  item: Field;
}

export const SortableItem = ({ id, item }: SortableItemProps): JSX.Element => {
  const itemId = id.toString();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <FieldRow field={item} draggableListeners={{ ...listeners }} />
    </div>
  );
};

const transformToApiFormat = (
  itemsMapping: Record<number, FieldSectionMapping>,
): FieldSection[] => {
  const fieldSectionsList: FieldSection[] = [];

  Object.keys(itemsMapping).forEach((fieldSection) => {
    fieldSectionsList.push({
      ...itemsMapping[fieldSection],
      fieldOrder: itemsMapping[fieldSection].fieldOrder.map(
        (field) => field.fieldId,
      ),
    });
  });

  return fieldSectionsList;
};
const computeContainerDetails = (
  itemId: string,
  itemsMap: Record<number, FieldSectionMapping>,
): [number, number | null, Field | null] => {
  let containerId;
  let itemIndex = null;
  let itemDetails = null;

  if (itemId in itemsMap) {
    // when item is dragged to an empty container, set the containerId alone.
    containerId = parseInt(itemId, 10);
  } else {
    Object.keys(itemsMap).forEach((key) => {
      itemsMap[key].fieldOrder.forEach((field, index) => {
        if (field?.fieldId && field.fieldId === parseInt(itemId, 10)) {
          containerId = itemsMap[key].fieldSectionId;
          itemIndex = index;
          itemDetails = field;
        }
      });
    });
  }

  return [containerId, itemIndex, itemDetails];
};

export const computeItemsOnDragOver = (
  activeId: string,
  overId: string,
  itemsMap: Record<number, FieldSectionMapping>,
): Record<number, FieldSectionMapping> | null => {
  const [overContainerId, overItemIndex] = computeContainerDetails(
    overId,
    itemsMap,
  );
  const [activeContainerId, , activeItem] = computeContainerDetails(
    activeId,
    itemsMap,
  );

  if (
    overContainerId &&
    activeContainerId &&
    activeContainerId !== overContainerId &&
    activeItem
  ) {
    let newIndex = -1;

    if (overId in itemsMap) {
      newIndex = itemsMap[overContainerId].fieldOrder.length + 1;
    } else {
      const isBelowLastItem =
        overItemIndex === itemsMap[overContainerId].fieldOrder.length - 1;
      const modifier = isBelowLastItem ? 1 : 0;

      newIndex =
        overItemIndex && overItemIndex >= 0
          ? overItemIndex + modifier
          : itemsMap[overContainerId].fieldOrder.length + 1;
    }

    return {
      ...itemsMap,
      [activeContainerId]: {
        ...itemsMap[activeContainerId],
        fieldOrder: itemsMap[activeContainerId].fieldOrder.filter(
          (item) => item !== activeItem,
        ),
      },
      [overContainerId]: {
        ...itemsMap[overContainerId],
        fieldOrder: [
          ...itemsMap[overContainerId].fieldOrder.slice(0, newIndex),
          activeItem,
          ...itemsMap[overContainerId].fieldOrder.slice(
            newIndex,
            itemsMap[overContainerId].fieldOrder.length,
          ),
        ],
      },
    };
  }

  return null;
};

export const computeItemsOnDragEnd = (
  activeId: string,
  overId: string,
  itemsMap: Record<number, FieldSectionMapping>,
): [
  Record<number, FieldSectionMapping> | undefined,
  number | undefined,
  number | undefined,
] => {
  const [overContainerId, overItemIndex] = computeContainerDetails(
    overId,
    itemsMap,
  );
  const [activeContainerId, activeItemIndex] = computeContainerDetails(
    activeId,
    itemsMap,
  );

  if (
    !activeContainerId ||
    !overContainerId ||
    activeItemIndex === null ||
    overItemIndex === null
  ) {
    return [undefined, undefined, undefined];
  }
  if (activeItemIndex !== overItemIndex || activeId === overId) {
    return [
      {
        ...itemsMap,
        [overContainerId]: {
          ...itemsMap[overContainerId],
          fieldOrder: arrayMove(
            itemsMap[overContainerId].fieldOrder,
            activeItemIndex,
            overItemIndex,
          ),
        },
      },
      overItemIndex,
      overContainerId,
    ];
  }

  return [undefined, undefined, undefined];
};

export const SortableSectionList = ({
  sectionList,
  sectionType,
}: Props): JSX.Element => {
  const [itemsMap, setItemsMap] = useState<Record<number, FieldSectionMapping>>(
    [],
  );

  useEffect(() => {
    setItemsMap(keyBy(sectionList, 'fieldSectionId'));
  }, [sectionList]);

  const [activeItemDetails, setActiveItemDetails] =
    useState<Field | null>(null);
  const [sourceId, setSourceId] = useState<string>('');
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const SectionContent = ({ fieldSection }): JSX.Element => {
    return (
      <>
        {fieldSection.fieldOrder.length ? (
          <>
            {fieldSection.fieldOrder.map((field) => {
              return field?.fieldId &&
                !(field.fieldName === 'taskDescription') ? (
                <SortableItem item={field} id={field?.fieldId} />
              ) : (
                <></>
              );
            })}
          </>
        ) : (
          <SectionTableEmptyState />
        )}
      </>
    );
  };

  const { mutate: updateFieldSectionsOrder } =
    useUpdateFieldSectionOrderMutation(sectionType);

  const errorTitle = useTranslation(
    'settings.fields.section.task.field.order.error.title',
  );

  let sectionFieldOrderUpdateError = useTranslation(
    'settings.fields.section.field.order.error.title',
  );

  const taskSectionFieldOrderUpdateError = useTranslation(
    'settings.fields.section.task.field.order.error.message',
  );

  const cannotReorderField = useTranslation(
    'settings.fields.section.task.field.cannot.reorder',
  );

  const cannotReorderFieldMessage = useTranslation(
    'settings.fields.section.task.field.cannot.reorder.message',
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={({ active }) => {
        const [containerId, , activeItem] = computeContainerDetails(
          active.id,
          itemsMap,
        );

        setSourceId(containerId.toString());
        setActiveItemDetails(activeItem);
      }}
      onDragOver={({ active, over }) => {
        const overId = over?.id;

        if (!overId || !active.id) {
          return;
        }

        const itemsMapping = computeItemsOnDragOver(
          active.id,
          overId,
          itemsMap,
        );

        if (itemsMapping) {
          setItemsMap(itemsMapping);
        }
      }}
      onDragEnd={({ active, over }) => {
        if (!over?.id || !active.id || !activeItemDetails) {
          return;
        }
        const [itemsMapping, fieldOrderIndex, fieldSectionId] =
          computeItemsOnDragEnd(active.id, over.id, itemsMap);

        if (
          itemsMapping &&
          fieldSectionId &&
          itemsMapping[fieldSectionId].private &&
          !activeItemDetails.private &&
          sectionType !== FieldSectionType.Project
        ) {
          toastService({
            title: cannotReorderField,
            message: cannotReorderFieldMessage,
          }).error();
          setActiveItemDetails(null);

          const itemsMappingBeforeDrop = computeItemsOnDragOver(
            active.id,
            sourceId,
            itemsMap,
          );

          setItemsMap(itemsMappingBeforeDrop ?? itemsMapping);

          return;
        }

        if (
          itemsMapping &&
          fieldOrderIndex !== undefined &&
          fieldSectionId !== undefined
        ) {
          updateFieldSectionsOrder(
            {
              updatedFieldSections: transformToApiFormat(itemsMapping),
              fieldId: activeItemDetails.fieldId,
              fieldSectionId,
              fieldOrderIndex,
            },
            {
              onError: (error: any) => {
                const errorResponse = error?.response;
                const expectedErrorMsg =
                  'Update cannot be performed for fixed field section';
                const actualErrorMsg = errorResponse?.data?.errors[0]?.reason;
                const statusCode = errorResponse?.status;
                const isFixedFieldReorderError =
                  statusCode === 400 && actualErrorMsg === expectedErrorMsg;

                if (isFixedFieldReorderError) {
                  sectionFieldOrderUpdateError =
                    taskSectionFieldOrderUpdateError;
                }
                toastService({
                  title: errorTitle,
                  message: sectionFieldOrderUpdateError,
                }).error();
              },
            },
          );
        }
        setActiveItemDetails(null);
      }}
    >
      <div>
        {Object.keys(itemsMap).map((key) => {
          const sectionId = parseInt(key, 10);
          const fieldSection = itemsMap[key];
          const fieldOrderItems = itemsMap[sectionId].fieldOrder.map(
            (field) => {
              return {
                ...field,
                id: field?.fieldId.toString(),
              };
            },
          );

          return (
            <SortableContext
              key={itemsMap[sectionId].fieldSectionId}
              items={fieldOrderItems}
              strategy={verticalListSortingStrategy}
            >
              {/* !isTaskFieldSection false condition can be removed/reverted once task-field section implementation feature is released
                    https://github.com/Rocketlane/rocket-frontend/pull/5694/files
              */}

              <FieldMappingContainer
                fieldSections={itemsMap}
                sectionId={sectionId}
              >
                <SectionContent fieldSection={fieldSection} />
              </FieldMappingContainer>
            </SortableContext>
          );
        })}
      </div>
      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeItemDetails ? <FieldRow field={activeItemDetails} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
